/**
 * Copilot Product Search API Service.
 *
 * Provides product catalog search and account product history
 * for the trip report review screen. Reuses the same
 * ProductSearchResult/Response types from the portal.
 */

import { copilotApi } from "../hooks/useApi";
import type {
  ProductSearchResponse,
  ProductSearchResult,
} from "../../services/portalProducts";

// Re-export for convenience
export type { ProductSearchResult, ProductSearchResponse };

export interface AccountProductHistoryItem {
  item_code: string;
  description: string | null;
  category: string | null;
  product_line: string | null;
  total_orders: number;
  total_quantity: number | null;
  last_ordered: string | null;
  sources: string[];
}

export interface AccountProductHistoryResponse {
  sage_customer_no: string | null;
  products: AccountProductHistoryItem[];
  total: number;
}

export const copilotProductService = {
  /**
   * Search products by item code or description.
   * Hits the copilot-scoped product search endpoint.
   */
  searchProducts: async (
    query: string,
    limit = 10,
  ): Promise<ProductSearchResponse> => {
    const response = await copilotApi.get<ProductSearchResponse>(
      "/copilot/products/search",
      { params: { q: query, limit } },
    );
    return response.data;
  },

  /**
   * Get products ordered by a specific account.
   */
  getAccountProductHistory: async (
    accountUuid: string,
  ): Promise<AccountProductHistoryResponse> => {
    const response = await copilotApi.get<AccountProductHistoryResponse>(
      `/copilot/products/account-history/${encodeURIComponent(accountUuid)}`,
    );
    return response.data;
  },
};
